import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowUpRight,
  ShoppingCart,
  QrCode,
  CheckCircle2,
  ChefHat,
  ReceiptText,
  Boxes,
  WifiOff,
  BarChart3,
  Building2,
  Star,
  Check,
  Menu,
  X,
  Users,
  ScanLine,
  CreditCard,
  ChevronDown,
  Sparkles,
  Zap,
  ShieldCheck,
  Globe2,
  Smartphone,
  Hotel,
  Utensils,
  Clock3,
  TrendingUp,
  BellRing,
  PackageCheck,
  Play,
  CircleCheck,
  Layers3,
  MessageCircle,
} from "lucide-react";

const FEATURES = [
  {
    icon: ShoppingCart,
    t: "Lightning POS",
    d: "Touch-first billing with split payments, discounts and holds — built for busy service hours.",
    tone: "orange",
  },
  {
    icon: QrCode,
    t: "QR Table Ordering",
    d: "Every table gets its own QR. Guests scan, browse the menu and order without installing an app.",
    tone: "purple",
  },
  {
    icon: CheckCircle2,
    t: "Order Approval",
    d: "Keep control at the right point. Authorized staff approve orders before they reach the kitchen.",
    tone: "blue",
  },
  {
    icon: ChefHat,
    t: "Kitchen Display",
    d: "Live KDS with station routing, timers and delay visibility so the kitchen knows what matters next.",
    tone: "rose",
  },
  {
    icon: ReceiptText,
    t: "Smart Billing",
    d: "Cash, UPI QR, card or gateway — with configurable GST and pay-now or pay-later workflows.",
    tone: "green",
  },
  {
    icon: Boxes,
    t: "Inventory & Recipes",
    d: "Track ingredients, recipes and serving availability while orders move through the operation.",
    tone: "amber",
  },
  {
    icon: WifiOff,
    t: "Offline-First",
    d: "Keep taking orders when the internet drops and sync safely when the connection returns.",
    tone: "cyan",
  },
  {
    icon: BarChart3,
    t: "Owner Analytics",
    d: "See sales, top items, peak hours and food-cost signals without jumping between systems.",
    tone: "indigo",
  },
  {
    icon: Building2,
    t: "Staff Management",
    d: "Manage staff roles, access and daily operations from one simple dashboard.",
    tone: "orange",
  },
];

const STEPS = [
  [
    "01",
    ScanLine,
    "Scan or Ring In",
    "A guest scans the table QR, or your captain/cashier punches the order into POS.",
  ],
  [
    "02",
    CheckCircle2,
    "Approve",
    "An authorized manager reviews the order. Once approved, the kitchen gets the KOT.",
  ],
  [
    "03",
    ChefHat,
    "Cook & Serve",
    "KDS routes the ticket to the right station, tracks timing and marks it ready for service.",
  ],
  [
    "04",
    CreditCard,
    "Pay & Analyze",
    "Collect payment, update inventory and turn the day's activity into useful reports.",
  ],
];

const PLANS = [
  {
    name: "Starter",
    price: "₹199",
    tag: "For restaurants that need simple POS & billing",
    available: true,
    feats: [
      "Dashboard",
      "POS Billing",
      "Orders",
      "Order History",
      "Menu Management",
      "Add / Edit / Delete Menu Items",
      "Restaurant Profile",
      "Payment & GST",
      "Restaurant QR Management",
      "Support Tickets",
    ],
  },

  {
    name: "Basic",
    price: "₹699",
    tag: "Complete restaurant management for everyday operations",
    available: true,
    featured: true,
    feats: [
      "Dashboard",
      "POS Billing",
      "Order Approvals",
      "Table Management",
      "Table QR & QR Ordering",
      "Orders & Order History",
      "Kitchen / KOT",
      "Preparing → Ready → Served",
      "Menu Management",
      "Inventory Management",
      "Customer Management",
      "Reservations",
      "Staff Management",
      "Staff Salary Management",
      "Expenses Management",
      "Reports & Analytics",
      "Payment & GST",
      "QR Management",
      "Role & Permissions",
      "Subscriptions",
      "Support Tickets",
    ],
  },

  {
    name: "Professional",
    tag: "Hotel + complete restaurant management",
    comingSoon: true,
  },

  {
    name: "Premium",
    tag: "Hotel + restaurant + mobile application",
    comingSoon: true,
  },
];

const FAQ = [
  [
    "Do my guests need to install an app?",
    "No. Guests scan the table QR and order from a mobile web menu instantly.",
  ],
  [
    "Does it work without internet?",
    "Yes. The POS is designed to keep the core workflow running offline and sync when the connection returns.",
  ],
  [
    "Can I stop orders going straight to the kitchen?",
    "Yes. Orders can require approval, with trusted roles able to send directly when your property chooses.",
  ],
  [
    "Is GST handled correctly?",
    "GST and billing rules can be configured according to your business setup.",
  ],
  [
    "What is the ₹99 trial?",
    "It is the low-risk starting point for trying the StayServe workflow. After the trial, you can continue on the monthly plan that fits your business.",
  ],
];

const NAV = [
  ["Platform", "#platform"],
  ["Features", "#features"],
  ["How it works", "#how"],
  ["Pricing", "#pricing"],
  ["Contact", "#contact"],
];

function Nav({ label, href, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="relative text-sm font-semibold text-stone-600 transition hover:text-stone-950 after:absolute after:-bottom-2 after:left-0 after:h-px after:w-0 after:bg-orange-500 after:transition-all hover:after:w-full"
    >
      {label}
    </a>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  light = false,
  center = false,
}) {
  return (
    <div className={`${center ? "mx-auto text-center" : ""} max-w-3xl`}>
      <p
        className={`text-xs font-bold uppercase tracking-[0.24em] ${
          light ? "text-orange-400" : "text-orange-500"
        }`}
      >
        {eyebrow}
      </p>

      <h2
        className={`mt-3 font-display text-4xl font-bold tracking-[-0.03em] sm:text-5xl ${
          light ? "text-white" : "text-stone-950"
        }`}
      >
        {title}
      </h2>

      <p
        className={`mt-5 max-w-2xl text-base leading-7 ${
          center ? "mx-auto" : ""
        } ${light ? "text-stone-400" : "text-stone-500"}`}
      >
        {description}
      </p>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="relative mx-auto w-full max-w-[690px]">
      <div className="absolute -inset-12 rounded-[50px] bg-orange-500/15 blur-3xl" />

      <div className="relative rounded-[30px] border border-white/15 bg-[#111214] p-2 shadow-[0_30px_100px_rgba(0,0,0,.45)]">
        <div className="rounded-[24px] border border-white/10 bg-[#171819]">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div className="flex items-center gap-3  px-3 py-1 rounded-xl w-fit">
              <img
                src="/assets/logo-white.png"
                alt="StayServe"
                className="h-8 w-auto object-contain"
              />

              <div>
                <div className="font-display text-[12px] font-black tracking-tight leading-none">
                  <span className="text-[#ffffff]">Stay</span>
                  <span className="text-[#FF6B00]">Serve</span>
                </div>

                <div className="mt-1 text-[5px] font-bold uppercase tracking-[.20em] text-[#ffffff]">
                  Hospitality operations
                </div>
              </div>
            </div>

            <div className="hidden items-center gap-5 text-[10px] text-stone-500 sm:flex">
              <span className="text-white">Overview</span>
              <span>Orders</span>
              <span>KDS</span>
              <span>Inventory</span>
            </div>

            <div className="h-7 w-7 rounded-full bg-orange-500/10" />
          </div>

          <div className="grid grid-cols-[54px_1fr]">
            <div className="border-r border-white/10 p-2">
              {[
                BarChart3,
                ShoppingCart,
                QrCode,
                ChefHat,
                Boxes,
                ReceiptText,
              ].map((Icon, i) => (
                <div
                  key={i}
                  className={`mb-2 grid h-9 place-items-center rounded-xl ${
                    i === 0
                      ? "bg-orange-500/15 text-orange-400"
                      : "text-stone-600"
                  }`}
                >
                  <Icon size={15} />
                </div>
              ))}
            </div>

            <div className="p-4 sm:p-5">
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <p className="text-[9px] text-stone-500">TODAY</p>
                  <h3 className="mt-1 text-lg font-bold text-white">
                    Restaurant overview
                  </h3>
                </div>

                <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-[9px] font-semibold text-emerald-400">
                  Live
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ["Today's Sales", "₹48,762"],
                  ["Orders", "128"],
                  ["Avg. Order", "₹381"],
                  ["Active Tables", "14 / 28"],
                ].map(([a, b]) => (
                  <div
                    key={a}
                    className="rounded-2xl border border-white/10 bg-white/[.035] p-3"
                  >
                    <p className="text-[8px] text-stone-500">{a}</p>
                    <p className="mt-1 text-sm font-bold text-white">{b}</p>
                    <p className="mt-1 text-[7px] text-orange-400">
                      +12.5% yesterday
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-5">
                <div className="rounded-2xl border border-white/10 bg-white/[.035] p-4 sm:col-span-3">
                  <div className="flex justify-between text-[9px] font-semibold text-stone-300">
                    <span>Sales trend</span>
                    <span className="text-orange-400">Last 7 days</span>
                  </div>

                  <div className="mt-5 flex h-28 items-end gap-2">
                    {[25, 38, 30, 54, 45, 68, 57, 80, 70, 92].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t-md bg-gradient-to-t from-orange-600 to-orange-400/70"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[.035] p-4 sm:col-span-2">
                  <p className="text-[9px] font-semibold text-stone-300">
                    Top selling items
                  </p>

                  <div className="mt-4 space-y-3 text-[8px] text-stone-400">
                    {[
                      "Butter Chicken|42",
                      "Veg Biryani|37",
                      "Paneer Tikka|32",
                      "Chicken Biryani|28",
                    ].map((x) => {
                      const [n, v] = x.split("|");

                      return (
                        <div key={n} className="flex justify-between">
                          <span>{n}</span>
                          <b className="text-white">{v}</b>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="rounded-xl border border-orange-500/20 bg-orange-500/[.07] p-3">
                  <p className="text-[8px] text-orange-300">KDS</p>
                  <p className="mt-1 text-xs font-bold">18 active tickets</p>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/[.035] p-3">
                  <p className="text-[8px] text-stone-500">Inventory</p>
                  <p className="mt-1 text-xs font-bold">94% healthy</p>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/[.035] p-3">
                  <p className="text-[8px] text-stone-500">Payments</p>
                  <p className="mt-1 text-xs font-bold">₹31.4K today</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-10 -right-3 w-[210px] rounded-[26px] border border-white/15 bg-[#171819] p-3 shadow-2xl shadow-black/50 sm:-right-7">
        <div className="rounded-[20px] border border-white/10 bg-[#0d0e0f] p-3">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-bold text-orange-400">
              Table 07
            </span>
            <span className="text-[8px] text-stone-600">QR order</span>
          </div>

          <div className="mt-4 space-y-2 text-[9px] text-stone-300">
            <div className="flex justify-between">
              <span>Masala Dosa</span>
              <span>₹120</span>
            </div>

            <div className="flex justify-between">
              <span>Paneer Tikka</span>
              <span>₹160</span>
            </div>

            <div className="flex justify-between">
              <span>Dal Makhani</span>
              <span>₹200</span>
            </div>
          </div>

          <button className="mt-4 w-full rounded-xl bg-orange-500 py-2.5 text-[9px] font-bold text-white">
            Place Order · ₹480
          </button>
        </div>
      </div>
    </div>
  );
}

function QRVisual() {
  return (
    <div className="relative mx-auto flex h-[360px] max-w-[430px] items-center justify-center overflow-hidden rounded-[34px] border border-stone-200 bg-gradient-to-br from-orange-50 via-white to-stone-100 shadow-[0_25px_70px_rgba(0,0,0,.10)]">
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-orange-200/40 blur-3xl" />

      <div className="relative w-[245px] rotate-[-3deg] rounded-[24px] border border-stone-200 bg-white p-5 shadow-2xl">
        <div className="flex items-center gap-2">
          <img
            src="/assets/logo.png"
            alt="StayServe"
            className="h-8 w-auto object-contain"
          />

          <div>
            <div className="font-display text-[12px] font-black tracking-tight leading-none">
              <span className="text-[#063B68]">Stay</span>
              <span className="text-[#FF6B00]">Serve</span>
            </div>

            <div className="mt-1 text-[5px] font-bold uppercase tracking-[.20em] text-[#063B68]">
              Hospitality operations
            </div>
          </div>
        </div>

        <p className="mt-5 text-[10px] font-semibold uppercase tracking-widest text-orange-500">
          Table 07
        </p>

        <h3 className="mt-1 text-2xl font-black">
          Scan. Order.
          <br />
          Enjoy.
        </h3>

        <div className="mx-auto mt-5 grid h-32 w-32 grid-cols-7 gap-1 rounded-xl border-4 border-stone-950 bg-white p-2">
          {Array.from({ length: 49 }).map((_, i) => (
            <span
              key={i}
              className={`${
                (i * 17 + i * i) % 7 < 3 ? "bg-stone-950" : "bg-white"
              } rounded-[1px]`}
            />
          ))}
        </div>

        <p className="mt-4 text-center text-[9px] text-stone-500">
          No app required · Mobile web menu
        </p>
      </div>
    </div>
  );
}

function PhoneVisual() {
  return (
    <div className="relative mx-auto h-[430px] max-w-[430px] overflow-hidden rounded-[34px] bg-[#0e0f10] shadow-[0_30px_90px_rgba(0,0,0,.35)]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(249,115,22,.25),transparent_35%)]" />

      <div className="absolute left-1/2 top-6 h-[390px] w-[205px] -translate-x-1/2 rounded-[34px] border-[6px] border-stone-700 bg-[#171819] p-2 shadow-2xl">
        <div className="h-full overflow-hidden rounded-[26px] bg-white">
          <div className="bg-[#111214] px-4 pb-5 pt-4 text-white">
            <div className="flex justify-between text-[8px]">
              <span>9:41</span>
              <span>StayServe</span>
            </div>

            <p className="mt-8 text-xl font-black">Good evening 👋</p>

            <p className="mt-1 text-[9px] text-stone-400">
              What would you like to order?
            </p>
          </div>

          <div className="space-y-2 p-3">
            {[
              "Butter Chicken · ₹320",
              "Paneer Tikka · ₹280",
              "Veg Biryani · ₹240",
            ].map((x, i) => (
              <div
                key={x}
                className="flex items-center gap-2 rounded-xl border border-stone-100 p-2"
              >
                <div
                  className={`h-9 w-9 rounded-lg ${
                    i === 0
                      ? "bg-orange-100"
                      : i === 1
                        ? "bg-amber-100"
                        : "bg-green-100"
                  }`}
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[9px] font-bold">{x}</p>
                  <p className="text-[7px] text-stone-400">
                    Chef's recommendation
                  </p>
                </div>

                <span className="text-[8px] font-bold text-orange-500">+</span>
              </div>
            ))}

            <button className="mt-2 w-full rounded-xl bg-orange-500 py-2.5 text-[9px] font-bold text-white">
              View cart · ₹840
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-7 left-6 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-md">
        <p className="text-[9px] font-bold text-orange-300">Coming soon</p>

        <p className="mt-1 text-xs font-semibold text-white">
          StayServe mobile app
        </p>
      </div>
    </div>
  );
}

function HotelVisual() {
  return (
    <div className="relative mx-auto h-[360px] max-w-[520px] overflow-hidden rounded-[34px] border border-stone-800 bg-[#101112] p-5 shadow-[0_30px_90px_rgba(0,0,0,.28)]">
      <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-orange-500">
            <Hotel size={15} />
          </div>

          <div>
            <p className="text-xs font-bold text-white">
              StayServe Hotel Panel
            </p>
            <p className="text-[8px] text-stone-500">
              Hospitality command center
            </p>
          </div>
        </div>

        <span className="rounded-full bg-orange-500/10 px-2.5 py-1 text-[8px] font-bold text-orange-300">
          COMING SOON
        </span>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2">
        {[
          ["Rooms", "84 / 120"],
          ["Check-ins", "18"],
          ["Occupancy", "70%"],
          ["Housekeeping", "12"],
          ["Bookings", "42"],
          ["Revenue", "₹2.8L"],
        ].map(([a, b]) => (
          <div
            key={a}
            className="rounded-2xl border border-white/10 bg-white/[.035] p-3"
          >
            <p className="text-[8px] text-stone-500">{a}</p>
            <p className="mt-1 text-sm font-bold text-white">{b}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-2xl border border-white/10 bg-white/[.035] p-4">
        <div className="flex items-center justify-between text-[9px]">
          <span className="font-bold text-white">Today's hotel flow</span>
          <span className="text-orange-400">Preview</span>
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {["Front desk", "Rooms", "Housekeeping", "Reports"].map((x, i) => (
            <div key={x} className="rounded-xl bg-white/[.04] p-2 text-center">
              <div className="mx-auto grid h-7 w-7 place-items-center rounded-lg bg-orange-500/10 text-orange-400">
                {React.createElement(
                  [Hotel, Users, PackageCheck, BarChart3][i],
                  { size: 12 },
                )}
              </div>

              <p className="mt-2 text-[7px] text-stone-400">{x}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LiveOrderStory() {
  return (
    <div className="relative mx-auto w-full max-w-[760px]">
      <div className="absolute -inset-16 rounded-[60px] bg-orange-500/10 blur-3xl" />

      <div className="relative overflow-hidden rounded-[34px] border border-white/10 bg-[#0b0c0d] p-5 shadow-[0_35px_100px_rgba(0,0,0,.45)] sm:p-7">
        <div className="flex items-center justify-between border-b border-white/10 pb-5">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.24em] text-orange-400">
              Live order journey
            </p>

            <h3 className="mt-1 font-display text-xl font-black text-white">
              Watch the order move.
            </h3>
          </div>

          <span className="flex items-center gap-2 rounded-full border border-emerald-400/15 bg-emerald-400/10 px-3 py-1.5 text-[9px] font-bold text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 ss-live-dot" />
            System live
          </span>
        </div>

        <div className="relative mt-7 min-h-[330px]">
          <div className="absolute left-[15%] right-[15%] top-[52%] hidden h-px bg-gradient-to-r from-orange-500/10 via-orange-500/50 to-orange-500/10 sm:block" />

          <div className="absolute left-[16%] top-[51%] hidden h-2 w-2 rounded-full bg-orange-400 shadow-[0_0_18px_rgba(249,115,22,.9)] sm:block ss-order-particle" />

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="relative rounded-[26px] border border-white/10 bg-white/[.035] p-5 ss-story-card ss-story-delay-1">
              <div className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-orange-500 text-[9px] font-black text-white shadow-lg shadow-orange-500/25">
                01
              </div>

              <div className="mx-auto grid h-28 w-28 place-items-center rounded-[24px] border border-white/10 bg-white/[.04]">
                <div className="grid h-20 w-20 grid-cols-7 gap-1 rounded-xl border-2 border-white/80 bg-white p-2">
                  {Array.from({ length: 49 }).map((_, i) => (
                    <span
                      key={i}
                      className={`${
                        (i * 17 + i * i) % 7 < 3 ? "bg-stone-950" : "bg-white"
                      } rounded-[1px]`}
                    />
                  ))}
                </div>
              </div>

              <p className="mt-5 text-center text-[9px] font-bold uppercase tracking-widest text-orange-400">
                Guest scans
              </p>

              <p className="mt-2 text-center text-xs leading-5 text-stone-500">
                Table 07 QR opens the menu instantly.
              </p>

              <div className="mt-4 flex justify-center">
                <span className="rounded-full bg-white/5 px-3 py-1.5 text-[8px] text-stone-400">
                  No app required
                </span>
              </div>
            </div>

            <div className="relative rounded-[26px] border border-orange-500/20 bg-orange-500/[.06] p-5 ss-story-card ss-story-delay-2">
              <div className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-orange-500 text-[9px] font-black text-white shadow-lg shadow-orange-500/25">
                02
              </div>

              <div className="relative mx-auto h-28 w-40 rounded-[22px] border border-white/10 bg-[#151617] p-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-md bg-orange-500" />
                  <div className="h-2 w-16 rounded-full bg-white/10" />
                </div>

                <div className="mt-4 rounded-xl border border-white/10 bg-white/[.035] p-2">
                  <div className="flex justify-between text-[7px]">
                    <span className="text-stone-400">Table 07</span>
                    <span className="text-orange-400">New order</span>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    <div className="flex justify-between text-[8px] text-white">
                      <span>Butter Chicken ×2</span>
                      <span>₹640</span>
                    </div>

                    <div className="flex justify-between text-[8px] text-stone-500">
                      <span>Paneer Tikka ×1</span>
                      <span>₹280</span>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-3 -right-3 rounded-full border border-orange-400/20 bg-orange-500 px-2.5 py-1 text-[7px] font-bold text-white shadow-lg">
                  ORDER IN
                </div>
              </div>

              <p className="mt-5 text-center text-[9px] font-bold uppercase tracking-widest text-orange-400">
                Order arrives
              </p>

              <p className="mt-2 text-center text-xs leading-5 text-stone-500">
                The business gets one clean order record.
              </p>

              <div className="mt-4 flex justify-center">
                <span className="rounded-full bg-orange-500/10 px-3 py-1.5 text-[8px] text-orange-300">
                  Approval ready
                </span>
              </div>
            </div>

            <div className="relative rounded-[26px] border border-white/10 bg-white/[.035] p-5 ss-story-card ss-story-delay-3">
              <div className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-orange-500 text-[9px] font-black text-white shadow-lg shadow-orange-500/25">
                03
              </div>

              <div className="mx-auto h-28 rounded-[22px] border border-white/10 bg-[#151617] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[7px] font-bold text-orange-400">
                    KDS · MAIN KITCHEN
                  </span>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </div>

                <div className="mt-4 rounded-xl border border-orange-500/20 bg-orange-500/[.06] p-3">
                  <div className="flex justify-between">
                    <span className="text-[8px] font-bold text-white">
                      #1042 · Table 07
                    </span>
                    <span className="text-[7px] text-orange-400">Cooking</span>
                  </div>

                  <p className="mt-2 text-[10px] font-bold text-white">
                    Butter Chicken ×2
                  </p>

                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <span className="ss-progress block h-full rounded-full bg-orange-500" />
                  </div>
                </div>
              </div>

              <p className="mt-5 text-center text-[9px] font-bold uppercase tracking-widest text-orange-400">
                Kitchen starts
              </p>

              <p className="mt-2 text-center text-xs leading-5 text-stone-500">
                The right station gets the ticket with timing.
              </p>

              <div className="mt-4 flex justify-center">
                <span className="rounded-full bg-emerald-400/10 px-3 py-1.5 text-[8px] text-emerald-300">
                  Ready to serve
                </span>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-1 left-1/2 hidden -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-white/[.04] px-4 py-2 sm:flex ss-bob">
            <BellRing size={12} className="text-orange-400" />
            <span className="text-[8px] font-semibold text-stone-400">
              One order · one source of truth · every team sees the same thing
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StationVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[560px]">
      <div className="absolute -inset-10 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative rounded-[30px] border border-white/10 bg-[#151617] p-5 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-orange-400">
              Kitchen station
            </p>

            <h3 className="mt-1 text-lg font-bold">Main Kitchen · Live KDS</h3>
          </div>

          <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[8px] font-bold text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Live
          </span>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            ["#1042", "Table 07", "Butter Chicken ×2", "8 min", "Cooking"],
            ["#1043", "Table 12", "Paneer Tikka ×1", "4 min", "New"],
            ["#1044", "Takeaway", "Veg Biryani ×3", "11 min", "Cooking"],
            ["#1045", "Table 03", "Dal Makhani ×2", "2 min", "Ready"],
          ].map(([id, table, item, time, status]) => (
            <div
              key={id}
              className="rounded-2xl border border-white/10 bg-white/[.035] p-4"
            >
              <div className="flex justify-between">
                <span className="text-[8px] text-stone-500">
                  {id} · {table}
                </span>

                <span
                  className={`text-[8px] font-bold ${
                    status === "Ready"
                      ? "text-emerald-400"
                      : status === "New"
                        ? "text-orange-400"
                        : "text-stone-400"
                  }`}
                >
                  {status}
                </span>
              </div>

              <p className="mt-3 text-sm font-bold">{item}</p>

              <div className="mt-3 flex items-center justify-between">
                <span className="flex items-center gap-1 text-[8px] text-stone-500">
                  <Clock3 size={11} />
                  {time}
                </span>

                <span className="h-1.5 w-20 overflow-hidden rounded-full bg-white/10">
                  <span className="block h-full w-3/5 rounded-full bg-orange-500" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openFaq, setOpenFaq] = React.useState(null);
  const [expandedPlans, setExpandedPlans] = React.useState({});
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-stone-950">
      <style>{`
        html {
          scroll-behavior: smooth;
        }

        @keyframes ss-float {
          0%,100% {
            transform: translateY(0)
          }
          50% {
            transform: translateY(-10px)
          }
        }

        @keyframes ss-float2 {
          0%,100% {
            transform: translateY(0) rotate(0)
          }
          50% {
            transform: translateY(-14px) rotate(1deg)
          }
        }

        @keyframes ss-pulse {
          0%,100% {
            box-shadow: 0 0 0 0 rgba(249,115,22,.25)
          }
          50% {
            box-shadow: 0 0 0 14px rgba(249,115,22,0)
          }
        }

        @keyframes ss-shine {
          0% {
            background-position: -180% 0
          }
          100% {
            background-position: 180% 0
          }
        }

        @keyframes ss-marquee {
          from {
            transform: translateX(0)
          }
          to {
            transform: translateX(-50%)
          }
        }

        @keyframes ss-bob {
          0%,100% {
            transform: translateY(0)
          }
          50% {
            transform: translateY(-5px)
          }
        }

        @keyframes ss-scan {
          0%,100% {
            transform: translateY(-34px);
            opacity: .2
          }
          45%,65% {
            opacity: 1
          }
          55% {
            transform: translateY(34px)
          }
        }

        @keyframes ss-particle {
          0% {
            left: 16%;
            opacity: 0;
            transform: scale(.7)
          }
          12% {
            opacity: 1
          }
          44% {
            left: 49%;
            opacity: 1;
            transform: scale(1)
          }
          50% {
            left: 51%;
            opacity: 0
          }
          62% {
            left: 51%;
            opacity: 0
          }
          82% {
            left: 83%;
            opacity: 1
          }
          100% {
            left: 86%;
            opacity: 0;
            transform: scale(.7)
          }
        }

        @keyframes ss-progress {
          0% {
            width: 8%
          }
          45% {
            width: 48%
          }
          75% {
            width: 78%
          }
          100% {
            width: 96%
          }
        }

        @keyframes ss-card-in {
          0%,8% {
            transform: translateY(12px);
            opacity: .35
          }
          18%,78% {
            transform: translateY(0);
            opacity: 1
          }
          90%,100% {
            transform: translateY(-5px);
            opacity: .75
          }
        }

        @keyframes ss-live {
          0%,100% {
            opacity: 1;
            transform: scale(1)
          }
          50% {
            opacity: .45;
            transform: scale(.72)
          }
        }

        @keyframes ss-ring {
          0% {
            box-shadow: 0 0 0 0 rgba(249,115,22,.35)
          }
          70% {
            box-shadow: 0 0 0 12px rgba(249,115,22,0)
          }
          100% {
            box-shadow: 0 0 0 0 rgba(249,115,22,0)
          }
        }

        .ss-float {
          animation: ss-float 5s ease-in-out infinite;
        }

        .ss-float2 {
          animation: ss-float2 7s ease-in-out infinite;
        }

        .ss-pulse {
          animation: ss-pulse 2.8s ease-in-out infinite;
        }

        .ss-shine {
          background-image: linear-gradient(
            110deg,
            transparent 20%,
            rgba(255,255,255,.18) 45%,
            transparent 70%
          );
          background-size: 200% 100%;
          animation: ss-shine 3.5s linear infinite;
        }

        .ss-marquee {
          animation: ss-marquee 28s linear infinite;
        }

        .ss-bob {
          animation: ss-bob 3.8s ease-in-out infinite;
        }

        .ss-order-particle {
          animation: ss-particle 5.4s cubic-bezier(.55,.1,.45,.9) infinite;
        }

        .ss-progress {
          animation: ss-progress 5.4s ease-in-out infinite;
        }

        .ss-story-card {
          animation: ss-card-in 5.4s ease-in-out infinite;
        }

        .ss-story-delay-2 {
          animation-delay: .22s;
        }

        .ss-story-delay-3 {
          animation-delay: .44s;
        }

        .ss-live-dot {
          animation: ss-live 1.8s ease-in-out infinite;
        }

        .ss-ring {
          animation: ss-ring 2.5s ease-out infinite;
        }

        /*
          PREMIUM BUTTON SYSTEM
          Compact + premium:
          - slightly smaller height
          - controlled horizontal padding
          - consistent radius
          - keeps the buttons from looking oversized
        */
        .ss-premium-btn {
          position: relative;
          min-height: 50px;
          border-radius: 14px;
          padding: 0 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 800;
          letter-spacing: -.01em;
          overflow: hidden;
          transition:
            transform .25s ease,
            box-shadow .25s ease,
            background-color .25s ease;
        }

        .ss-premium-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 40px rgba(249,115,22,.22);
        }

        .ss-premium-btn:active {
          transform: translateY(0) scale(.985);
        }

        .ss-premium-btn::after {
          content: "";
          position: absolute;
          inset: 1px;
          border-radius: 13px;
          background: linear-gradient(
            180deg,
            rgba(255,255,255,.14),
            transparent 45%
          );
          pointer-events: none;
        }

        @media(prefers-reduced-motion:reduce) {
          html {
            scroll-behavior: auto;
          }

          .ss-float,
          .ss-float2,
          .ss-pulse,
          .ss-shine,
          .ss-marquee,
          .ss-bob,
          .ss-order-particle,
          .ss-progress,
          .ss-story-card,
          .ss-live-dot,
          .ss-ring {
            animation: none !important;
          }

          .ss-premium-btn {
            transition: none;
          }

          .ss-premium-btn:hover {
            transform: none;
          }
        }
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-stone-200/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-[1400px] items-center justify-between px-5 lg:px-10">
          <a href="#top" className="flex items-center gap-3">
            <img
              src="/assets/logo.png"
              alt="StayServe"
              className="h-16 w-auto object-contain"
            />

            <div>
              <div className="font-display text-[26px] font-black tracking-tight leading-none">
                <span className="text-[#063B68]">Stay</span>
                <span className="text-[#FF6B00]">Serve</span>
              </div>

              <div className="mt-1 text-[10px] font-bold uppercase tracking-[.20em] text-[#063B68]">
                Hospitality operations
              </div>
            </div>
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map(([label, href]) => (
              <Nav key={label} label={label} href={href} />
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <Link to="/login">
              <Button
                variant="ghost"
                className="rounded-xl px-4 text-stone-700 hover:bg-stone-100"
              >
                Login
              </Button>
            </Link>

            <Link to="/signup">
              <Button className="ss-premium-btn ss-shine bg-orange-500 px-5 text-white shadow-lg shadow-orange-500/20 hover:bg-orange-600">
                Start ₹99 Trial
                <ArrowRight size={16} />
              </Button>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-xl border border-stone-200 md:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-stone-200 bg-white px-5 py-5 md:hidden">
            <nav className="flex flex-col gap-5">
              {NAV.map(([label, href]) => (
                <Nav
                  key={label}
                  label={label}
                  href={href}
                  onClick={closeMobile}
                />
              ))}
            </nav>

            <div className="mt-5 flex gap-2">
              <Link to="/login" className="flex-1" onClick={closeMobile}>
                <Button variant="outline" className="w-full rounded-xl">
                  Login
                </Button>
              </Link>

              <Link to="/signup" className="flex-1" onClick={closeMobile}>
                <Button className="ss-premium-btn w-full bg-orange-500 text-white">
                  Start ₹99 Trial
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main id="top">
        {/* HERO */}
        <section className="relative overflow-hidden bg-[#0a0b0c] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_34%,rgba(249,115,22,.18),transparent_31%),radial-gradient(circle_at_8%_100%,rgba(255,255,255,.055),transparent_27%)]" />

          <div className="absolute right-[9%] top-[20%] h-2 w-2 rounded-full bg-orange-400 ss-pulse" />

          <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 py-20 sm:py-24 lg:grid-cols-[.83fr_1.17fr] lg:px-10 lg:py-28">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[.05] px-4 py-2 text-xs font-semibold text-stone-300">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                The operating platform for modern hospitality
              </div>

              <h1 className="mt-7 max-w-2xl font-display text-[48px] font-black leading-[.94] tracking-[-.045em] sm:text-6xl lg:text-[78px]">
                Run your entire hospitality operation from{" "}
                <span className="text-orange-500">one screen.</span>
              </h1>

              <p className="mt-7 max-w-xl text-base leading-8 text-stone-300 sm:text-lg">
                From the guest's first QR scan to the kitchen ticket, payment,
                inventory and owner report — StayServe connects guest
                experience, operations, billing and reporting in one operating
                rhythm.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <span className="ss-ring inline-flex items-center gap-2 rounded-full border border-orange-400/20 bg-orange-500/10 px-3 py-2 text-xs font-bold text-orange-300">
                  <Sparkles size={13} />
                  Start with a ₹99 trial
                </span>

                <span className="text-xs text-stone-500">
                  Then choose the monthly plan that fits your business.
                </span>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/signup">
                  <Button className="ss-premium-btn ss-shine min-h-[52px] bg-orange-500 px-7 text-base text-white shadow-xl shadow-orange-500/25 hover:bg-orange-600">
                    Start ₹99 Trial
                    <ArrowRight size={17} />
                  </Button>
                </Link>

                <a href="#platform">
                  <Button
                    variant="outline"
                    className="ss-premium-btn min-h-[52px] border-white/15 bg-white/[.03] px-7 text-base text-white hover:bg-white/10"
                  >
                    See how it works
                    <ArrowUpRight size={16} />
                  </Button>
                </a>
              </div>

              <div className="mt-10 grid max-w-xl grid-cols-3 gap-5 border-t border-white/10 pt-7">
                {[
                  ["10 min", "To set up"],
                  ["Coming Soon", "Guest mobile app"],
                  ["100%", "Offline-ready POS"],
                ].map(([a, b]) => (
                  <div key={b}>
                    <div className="font-display text-2xl font-black text-orange-400">
                      {a}
                    </div>

                    <div className="mt-1 text-xs text-stone-500">{b}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10 ss-float2">
              <DashboardMockup />
            </div>
          </div>
        </section>

        {/* STORY / PLATFORM */}
        <section id="platform" className="bg-stone-50">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              eyebrow="One connected system"
              title="Your hospitality business has many moving parts. Your software shouldn't."
              description="StayServe is designed around the real journey of a hospitality business — guest ordering, staff control, kitchen execution, billing, stock and management reporting. Each layer feeds the next instead of creating another isolated screen."
            />

            <div className="mt-14 grid gap-5 lg:grid-cols-3">
              {[
                [
                  QrCode,
                  "Guest experience",
                  "Make ordering feel effortless",
                  "QR menus, mobile ordering and table-level context let guests order quickly without downloading an app.",
                  "bg-orange-50 text-orange-500",
                ],
                [
                  ChefHat,
                  "Hospitality operations",
                  "Keep the team in sync",
                  "POS, approvals, KDS, billing and inventory work together so the front-of-house and kitchen share the same order truth.",
                  "bg-stone-900 text-orange-400",
                ],
                [
                  BarChart3,
                  "Owner control",
                  "See what is happening",
                  "Reports and analytics turn day-to-day activity into a clearer picture of sales, items, tables and operations.",
                  "bg-white text-orange-500",
                ],
              ].map(([Icon, eyebrow, title, desc, iconClass]) => (
                <div
                  key={title}
                  className="group rounded-[28px] border border-stone-200 bg-white p-7 shadow-sm transition duration-500 hover:-translate-y-2 hover:shadow-2xl"
                >
                  <div
                    className={`grid h-14 w-14 place-items-center rounded-2xl ${iconClass} transition duration-500 group-hover:scale-110`}
                  >
                    <Icon size={24} />
                  </div>

                  <p className="mt-7 text-xs font-bold uppercase tracking-widest text-orange-500">
                    {eyebrow}
                  </p>

                  <h3 className="mt-2 font-display text-2xl font-black">
                    {title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-stone-500">
                    {desc}
                  </p>

                  <a
                    href="#features"
                    className="mt-6 inline-flex items-center text-xs font-bold text-stone-900"
                  >
                    Explore this layer
                    <ArrowRight
                      size={13}
                      className="ml-2 transition group-hover:translate-x-1"
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* QR STORY */}
        <section className="bg-white">
          <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 py-20 lg:grid-cols-2 lg:px-10 lg:py-28">
            <div>
              <SectionHeading
                eyebrow="01 · Guest ordering"
                title="A QR code becomes the first touchpoint of the guest experience."
                description="Put a unique QR on every table. Guests scan, open the menu, choose items and send the order — without installing an app. The order then enters your controlled operating workflow."
              />

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  ["Scan", "Table-specific QR"],
                  ["Browse", "Mobile menu"],
                  ["Order", "Direct to workflow"],
                ].map(([a, b]) => (
                  <div
                    key={a}
                    className="rounded-2xl border border-stone-200 bg-stone-50 p-4"
                  >
                    <p className="text-sm font-bold">{a}</p>
                    <p className="mt-1 text-xs text-stone-500">{b}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="ss-float">
              <QRVisual />
            </div>
          </div>
        </section>

        {/* LIVE ORDER JOURNEY */}
        <section className="relative overflow-hidden bg-[#0c0d0e] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(249,115,22,.12),transparent_42%)]" />

          <div className="relative mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              light
              center
              eyebrow="01.5 · The magic moment"
              title="Don't just explain the workflow. Let the owner watch it happen."
              description="A guest scans the table. The order appears. Approval happens. The kitchen starts cooking. This is the kind of motion that makes the product feel real before a single demo call."
            />

            <div className="mt-14">
              <LiveOrderStory />
            </div>
          </div>
        </section>

        {/* KITCHEN / STATION */}
        <section className="bg-[#0c0d0e] text-white">
          <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-20 lg:grid-cols-2 lg:px-10 lg:pb-28">
            <div className="order-2 lg:order-1">
              <StationVisual />
            </div>

            <div className="order-1 lg:order-2">
              <SectionHeading
                light
                eyebrow="02 · Kitchen stations"
                title="From approved order to the right station — without the chaos."
                description="The kitchen display is not just a screen of orders. It becomes the operating layer for stations: new tickets, preparation time, status and readiness — all visible to the team."
              />

              <div className="mt-8 space-y-4">
                {[
                  [
                    "Live KDS",
                    "See active tickets as they move through the kitchen.",
                  ],
                  [
                    "Station visibility",
                    "Keep the right work visible to the right team.",
                  ],
                  ["Timing", "Track preparation and spot delays earlier."],
                  [
                    "Ready to serve",
                    "Give front-of-house a clear signal when food is ready.",
                  ],
                ].map(([a, b]) => (
                  <div
                    key={a}
                    className="flex gap-4 rounded-2xl border border-white/10 bg-white/[.035] p-4"
                  >
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-orange-500/10 text-orange-400">
                      <CircleCheck size={17} />
                    </div>

                    <div>
                      <p className="text-sm font-bold">{a}</p>
                      <p className="mt-1 text-xs leading-5 text-stone-500">
                        {b}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" className="bg-white">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              center
              eyebrow="03 · The full stack"
              title="Every feature has a job. Every job connects."
              description="Instead of stuffing everything into one dashboard, StayServe gives each business function its own clear role — while keeping the underlying data connected."
            />

            <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f) => (
                <div
                  key={f.t}
                  className="group rounded-[26px] border border-stone-200 bg-white p-7 transition duration-500 hover:-translate-y-2 hover:border-orange-200 hover:shadow-[0_24px_60px_rgba(0,0,0,.09)]"
                >
                  <div className="flex items-start justify-between">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-orange-500 transition duration-500 group-hover:scale-110 group-hover:bg-orange-500 group-hover:text-white">
                      <f.icon size={21} />
                    </div>

                    <ArrowUpRight
                      size={16}
                      className="text-stone-300 transition group-hover:text-orange-500"
                    />
                  </div>

                  <h3 className="mt-6 font-display text-xl font-black">
                    {f.t}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-stone-500">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MOBILE + HOTEL ROADMAP */}
        <section className="bg-stone-50">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              center
              eyebrow="04 · What's next"
              title="The platform is growing beyond the restaurant floor."
              description="The core hospitality workflow comes first. Around it, the StayServe ecosystem can grow into guest mobile experiences and hospitality operations."
            />

            <div className="mt-14 grid gap-6 lg:grid-cols-2">
              <div className="overflow-hidden rounded-[32px] border border-stone-200 bg-white shadow-sm">
                <div className="p-8 pb-0">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full bg-orange-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-orange-500">
                      Coming soon
                    </span>

                    <Smartphone className="text-stone-300" />
                  </div>

                  <h3 className="mt-5 font-display text-3xl font-black">
                    StayServe Mobile App
                  </h3>

                  <p className="mt-3 max-w-lg text-sm leading-7 text-stone-500">
                    A dedicated mobile experience for the next stage of guest
                    and team workflows — designed to sit naturally on top of the
                    same StayServe ecosystem.
                  </p>
                </div>

                <div className="mt-8">
                  <PhoneVisual />
                </div>
              </div>

              <div className="overflow-hidden rounded-[32px] border border-stone-800 bg-[#101112] text-white shadow-sm">
                <div className="p-8 pb-0">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full bg-orange-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-orange-300">
                      Coming soon
                    </span>

                    <Hotel className="text-stone-600" />
                  </div>

                  <h3 className="mt-5 font-display text-3xl font-black">
                    StayServe Hotel Panel
                  </h3>

                  <p className="mt-3 max-w-lg text-sm leading-7 text-stone-400">
                    Extend the operating philosophy into hospitality: rooms,
                    check-ins, housekeeping, bookings and hotel-level reporting
                    in one future-ready control panel.
                  </p>
                </div>

                <div className="mt-8 px-5 pb-6">
                  <HotelVisual />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW */}
        <section id="how" className="bg-white">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              center
              eyebrow="05 · One workflow"
              title="A simple story from scan to sale."
              description="The point is not to make your staff learn more software. The point is to make the business's existing workflow easier to see and control."
            />

            <div className="relative mt-14 grid gap-5 md:grid-cols-4">
              <div className="absolute left-[12%] right-[12%] top-10 hidden h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent md:block" />

              {STEPS.map(([num, Icon, title, desc]) => (
                <div
                  key={num}
                  className="group relative rounded-[26px] border border-stone-200 bg-stone-50 p-7 transition duration-500 hover:-translate-y-2 hover:bg-white hover:shadow-xl"
                >
                  <div className="relative grid h-12 w-12 place-items-center rounded-2xl bg-white text-orange-500 shadow-sm ring-1 ring-stone-200 group-hover:bg-orange-500 group-hover:text-white">
                    <Icon size={20} />
                  </div>

                  <div className="mt-7 flex items-center justify-between">
                    <h3 className="font-display text-xl font-black">{title}</h3>

                    <span className="font-display text-3xl font-black text-stone-200">
                      {num}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-7 text-stone-500">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTROL STORY */}
        <section className="bg-[#0c0d0e] text-white">
          <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 py-20 lg:grid-cols-[.85fr_1.15fr] lg:px-10 lg:py-28">
            <div>
              <SectionHeading
                light
                eyebrow="06 · Owner control"
                title="Know what is happening — without standing at the counter."
                description="Once the order, kitchen and billing data live in one flow, the owner gets a different kind of visibility: what sold, what is moving, what is active and where attention is needed."
              />

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {[
                  ["Sales", "Track revenue and order movement."],
                  ["Top items", "See what customers are actually choosing."],
                  ["Tables", "Understand active and available tables."],
                  [
                    "Inventory",
                    "Keep ingredients and recipes in the same operating picture.",
                  ],
                ].map(([a, b]) => (
                  <div
                    key={a}
                    className="rounded-2xl border border-white/10 bg-white/[.035] p-4"
                  >
                    <div className="flex items-center gap-2 text-orange-400">
                      <TrendingUp size={14} />
                      <span className="text-xs font-bold">{a}</span>
                    </div>

                    <p className="mt-2 text-xs leading-5 text-stone-500">{b}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="ss-float">
              <DashboardMockup />
            </div>
          </div>
        </section>

        {/* ₹99 + PRICING */}
        <section id="pricing" className="bg-stone-50">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <div className="relative overflow-hidden rounded-[34px] bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 p-8 text-white shadow-2xl shadow-orange-500/20 sm:p-12 lg:p-14">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

              <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto]">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold">
                    <Zap size={13} />
                    Low-risk starting point
                  </div>

                  <h2 className="mt-5 font-display text-4xl font-black sm:text-5xl">
                    Start your StayServe journey for ₹99.
                  </h2>

                  <p className="mt-4 max-w-2xl text-sm leading-7 text-orange-50/90">
                    Try the workflow first. Then continue with the monthly plan
                    that matches your restaurant — Basic, Professional or
                    Premium.
                  </p>
                </div>

                <Link to="/signup">
                  <Button className="ss-premium-btn ss-shine min-h-[50px] bg-white px-6 text-orange-600 hover:bg-orange-50">
                    Start ₹99 Trial
                    <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="mt-20">
              <SectionHeading
                center
                eyebrow="07 · Plans"
                title="Simple, transparent pricing."
                description="Start with the ₹99 trial, then choose the monthly plan that fits the size and ambition of your business."
              />

              <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {PLANS.map((p) => {
                  const isExpanded = !!expandedPlans[p.name];
                  const visibleFeatures = p.feats
                    ? isExpanded
                      ? p.feats
                      : p.feats.slice(0, 5)
                    : [];
                  const hasMoreFeatures = p.available && p.feats?.length > 5;

                  return (
                    <div
                      key={p.name}
                      className={`relative flex h-full flex-col rounded-[30px] border p-8 transition duration-500 hover:-translate-y-2 ${
                        p.featured
                          ? "border-orange-500 bg-white shadow-2xl shadow-orange-500/10"
                          : "border-stone-200 bg-white"
                      }`}
                    >
                      {/* Badges */}

                      {p.featured && (
                        <span className="absolute right-5 top-5 rounded-full bg-orange-500 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-white">
                          Most Popular
                        </span>
                      )}

                      {p.comingSoon && (
                        <span className="absolute left-5 top-5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-orange-600">
                          Coming Soon
                        </span>
                      )}

                      {p.available && (
                        <span className="absolute right-5 top-5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-700">
                          Available Now
                        </span>
                      )}

                      {/* Plan heading */}

                      <div className="flex items-start justify-between gap-3 pt-8 pr-20 sm:pr-24">
                        <div>
                          <h3 className="font-display text-2xl font-black">
                            {p.name}
                          </h3>

                          <p className="mt-1 text-xs leading-5 text-stone-500">
                            {p.tag}
                          </p>
                        </div>
                      </div>

                      {/* Price — only show pricing for currently available plans */}

                      {p.available && p.price && (
                        <div className="mt-7">
                          <span className="font-display text-5xl font-black">
                            {p.price}
                          </span>

                          <span className="text-sm text-stone-400">
                            {" "}
                            / month
                          </span>
                        </div>
                      )}

                      {/* Features */}

                      {p.available && (
                        <ul className="mt-8 flex-1 space-y-3">
                          {visibleFeatures.map((f) => (
                            <li
                              key={f}
                              className="flex gap-2 text-sm text-stone-600"
                            >
                              <Check
                                size={16}
                                className="mt-0.5 shrink-0 text-orange-500"
                              />

                              <span>{f}</span>
                            </li>
                          ))}

                          {hasMoreFeatures && (
                            <li>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedPlans((current) => ({
                                    ...current,
                                    [p.name]: !isExpanded,
                                  }))
                                }
                                className="mt-1 text-xs font-black text-orange-600 transition hover:text-orange-700 hover:underline"
                              >
                                {isExpanded
                                  ? "Show Less"
                                  : `Many more (${p.feats.length - 5})`}
                              </button>
                            </li>
                          )}
                        </ul>
                      )}

                      {/* CTA */}

                      {p.available ? (
                        <Link
                          to={`/signup?plan=${
                            p.name === "Starter" ? "starter" : "basic"
                          }`}
                        >
                          <Button className="ss-premium-btn mt-9 min-h-[54px] w-full bg-orange-500 text-white hover:bg-orange-600">
                            Start {p.name}
                            <ArrowRight size={15} />
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          disabled
                          className="mt-9 min-h-[54px] w-full cursor-not-allowed rounded-[14px] bg-stone-100 font-bold text-stone-500 opacity-100"
                        >
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-7 flex flex-wrap justify-center gap-7 text-xs text-stone-500">
                <span>✓ ₹99 trial</span>
                <span>✓ No setup fee</span>
                <span>✓ Cancel anytime</span>
                <span>✓ Secure payments</span>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="bg-[#111214] text-white">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              light
              center
              eyebrow="08 · Restaurant stories"
              title="Built around the way hospitality businesses actually work."
              description="The experience should feel calm for the owner, clear for the team and effortless for the guest."
            />

            <div className="mt-12 grid gap-5 lg:grid-cols-3">
              {[
                [
                  "StayServe has simplified our operations like never before. Billing, KOT, inventory — everything just works!",
                  "Rohit Sharma",
                  "Restaurant Owner",
                ],
                [
                  "The QR ordering and live kitchen tracking have improved our service speed dramatically. Highly recommended!",
                  "Anjali Mehta",
                  "Manager, Spice Hub",
                ],
                [
                  "Great system, excellent support, and very easy to use. Our staff adapted in just one day.",
                  "Vikram Singh",
                  "Owner, Grill Nation",
                ],
              ].map(([q, n, r]) => (
                <div
                  key={n}
                  className="rounded-[26px] border border-white/10 bg-white/[.035] p-7 transition duration-500 hover:-translate-y-2 hover:border-orange-500/30"
                >
                  <div className="flex gap-1 text-orange-400">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={15} fill="currentColor" />
                    ))}
                  </div>

                  <p className="mt-5 text-sm leading-7 text-stone-300">“{q}”</p>

                  <div className="mt-7 border-t border-white/10 pt-5">
                    <p className="text-sm font-bold">{n}</p>
                    <p className="mt-1 text-xs text-stone-500">{r}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-stone-200 bg-stone-50">
          <div className="mx-auto max-w-[1100px] px-5 py-20 lg:px-10 lg:py-28">
            <SectionHeading
              center
              eyebrow="09 · Questions"
              title="Everything you want to know before you start."
              description="Keep the important answers close to the decision — especially around ordering, connectivity, setup and the ₹99 trial."
            />

            <div className="mt-12 space-y-3">
              {FAQ.map(([q, a], i) => (
                <button
                  type="button"
                  key={q}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="group w-full rounded-[22px] border border-stone-200 bg-stone-50 p-5 text-left transition hover:border-orange-200 hover:bg-white hover:shadow-lg"
                >
                  <div className="flex items-center justify-between gap-5">
                    <span className="font-display text-base font-black">
                      {q}
                    </span>

                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-stone-400 transition ${
                        openFaq === i ? "rotate-180 text-orange-500" : ""
                      }`}
                    />
                  </div>

                  {openFaq === i && (
                    <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-500">
                      {a}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT / LOCATION */}
        <section id="contact" className="border-t border-stone-200 bg-white">
          <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
            <div className="grid items-stretch gap-8 lg:grid-cols-2">
              {/* LEFT — GOOGLE MAP + LOCATION */}
              <div className="flex h-full flex-col overflow-hidden rounded-[30px] border border-stone-200 bg-stone-50 shadow-sm">
                <div className="p-7 sm:p-8">
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-orange-500">
                    Visit us
                  </p>

                  <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.03em] sm:text-4xl">
                    Find SSQUARE LOGIC
                  </h2>

                  <p className="mt-3 max-w-xl text-sm leading-7 text-stone-500">
                    Have a question, want a product demo, or just want to know
                    how StayServe can fit your restaurant, hotel or hospitality
                    business? Come say hello or send us a message.
                  </p>

                  {/* LOCATION INFO */}
                  <div className="mt-7 grid gap-4 sm:grid-cols-2">
                    <div className="flex gap-3 rounded-2xl border border-stone-200 bg-white p-4">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-50 text-orange-500">
                        <Building2 size={18} />
                      </div>

                      <div>
                        <p className="text-xs font-bold text-stone-900">
                          Office
                        </p>

                        <p className="mt-1 text-xs leading-5 text-stone-500">
                          SSQUARE LOGIC
                          <br />
                          Benar Road, Jhotwara
                          <br />
                          Jaipur, Rajasthan 302012
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 rounded-2xl border border-stone-200 bg-white p-4">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-50 text-orange-500">
                        <Clock3 size={18} />
                      </div>

                      <div>
                        <p className="text-xs font-bold text-stone-900">
                          Business Hours
                        </p>

                        <p className="mt-1 text-xs leading-5 text-stone-500">
                          Monday – Saturday
                          <br />
                          10:00 AM – 7:00 PM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* GOOGLE MAP */}
                <div className="relative min-h-[360px] flex-1 w-full border-t border-stone-200 bg-stone-100">
                  <iframe
                    title="SSQUARE LOGIC Office Location"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15368.308175773993!2d75.74550659804214!3d26.973018841218394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1787042752799!5m2!1sen!2sin"
                    className="absolute inset-0 h-full w-full border-0 grayscale-[20%]"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                <div className="flex flex-wrap gap-3 p-6 sm:p-7">
                  <a
                    href="https://maps.app.goo.gl/o8C2mo5m5fvQFZyAA"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-stone-950 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-orange-500"
                  >
                    <Globe2 size={14} />
                    Open in Google Maps
                    <ArrowUpRight size={13} />
                  </a>

                  <a
                    href="tel:+919999999999"
                    className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-xs font-bold text-stone-700 transition hover:border-orange-200 hover:text-orange-500"
                  >
                    Call us
                  </a>
                </div>
              </div>

              {/* RIGHT — CONTACT FORM */}
              <div className="flex h-full flex-col rounded-[30px] border border-stone-200 bg-stone-50 p-7 shadow-sm sm:p-9">
                <div className="max-w-xl">
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-orange-500">
                    Contact us
                  </p>

                  <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.03em] sm:text-4xl">
                    Let's talk about your business.
                  </h2>

                  <p className="mt-3 text-sm leading-7 text-stone-500">
                    Tell us a little about your business and what you are
                    looking to improve. Our team will get back to you shortly.
                  </p>
                </div>

                <form
                  className="mt-8 space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();

                    alert("Thank you! Your message has been submitted.");
                  }}
                >
                  {/* NAME + PHONE */}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-bold text-stone-700">
                        Full Name *
                      </label>

                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-bold text-stone-700">
                        Phone Number *
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+91 98765 43210"
                        className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                      />
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div>
                    <label className="mb-2 block text-xs font-bold text-stone-700">
                      Email Address *
                    </label>

                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@business.com"
                      className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                    />
                  </div>

                  {/* RESTAURANT */}
                  <div>
                    <label className="mb-2 block text-xs font-bold text-stone-700">
                      Business / Property Name *
                    </label>

                    <input
                      type="text"
                      name="restaurant"
                      required
                      placeholder="Your business or property name"
                      className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                    />
                  </div>

                  {/* CITY + OUTLET */}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-bold text-stone-700">
                        City
                      </label>

                      <input
                        type="text"
                        name="city"
                        placeholder="Delhi, Mumbai, Bengaluru..."
                        className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-bold text-stone-700">
                        Number of Locations
                      </label>

                      <select
                        name="outlets"
                        className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm text-stone-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Select
                        </option>

                        <option value="1">1 Location</option>

                        <option value="2-5">2–5 Locations</option>

                        <option value="6-10">6–10 Locations</option>

                        <option value="10+">10+ Locations</option>
                      </select>
                    </div>
                  </div>

                  {/* SUBJECT */}
                  <div>
                    <label className="mb-2 block text-xs font-bold text-stone-700">
                      What can we help you with?
                    </label>

                    <select
                      name="subject"
                      defaultValue=""
                      className="h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm text-stone-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                    >
                      <option value="" disabled>
                        Select an option
                      </option>

                      <option value="demo">Book a product demo</option>

                      <option value="trial">Start ₹99 Trial</option>

                      <option value="pricing">Pricing enquiry</option>

                      <option value="support">Product support</option>

                      <option value="partnership">
                        Partnership / Business enquiry
                      </option>

                      <option value="other">Something else</option>
                    </select>
                  </div>

                  {/* MESSAGE */}
                  <div>
                    <label className="mb-2 block text-xs font-bold text-stone-700">
                      Message
                    </label>

                    <textarea
                      name="message"
                      rows={5}
                      placeholder="Tell us what you need..."
                      className="w-full resize-none rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10"
                    />
                  </div>

                  {/* CONSENT */}
                  <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-stone-500">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 h-4 w-4 rounded border-stone-300 accent-orange-500"
                    />

                    <span>
                      I agree to be contacted by the StayServe team regarding my
                      enquiry.
                    </span>
                  </label>

                  {/* SUBMIT */}
                  <Button
                    type="submit"
                    className="ss-premium-btn ss-shine min-h-[52px] w-full bg-orange-500 text-sm text-white shadow-lg shadow-orange-500/20 hover:bg-orange-600"
                  >
                    Send Message
                    <ArrowRight size={16} />
                  </Button>

                  <div className="flex flex-wrap items-center justify-center gap-5 pt-1 text-[10px] text-stone-400">
                    <span className="flex items-center gap-1.5">
                      <ShieldCheck size={12} className="text-orange-500" />
                      Your details are safe
                    </span>

                    <span className="flex items-center gap-1.5">
                      <Clock3 size={12} className="text-orange-500" />
                      Quick response
                    </span>

                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 size={12} className="text-orange-500" />
                      No spam
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-stone-50 px-5 pb-10 lg:px-10 lg:pb-16">
          <div className="mx-auto max-w-[1400px] overflow-hidden rounded-[34px] bg-[#0c0d0e] px-7 py-14 text-center text-white shadow-2xl sm:px-14 lg:py-20">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-400 ss-bob">
              <Layers3 size={25} />
            </div>

            <h2 className="mt-6 font-display text-4xl font-black sm:text-5xl">
              Ready to put the whole restaurant on one rhythm?
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-stone-400">
              Start with the ₹99 trial, explore the workflow and see how
              StayServe can bring guest experience, operations, billing,
              inventory and reporting together across restaurants and hotels.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup">
                <Button className="ss-premium-btn ss-shine min-h-[50px] bg-orange-500 px-7 text-white hover:bg-orange-600">
                  Start ₹99 Trial
                  <ArrowRight size={16} />
                </Button>
              </Link>

              <a href="#features">
                <Button
                  variant="outline"
                  className="ss-premium-btn min-h-[50px] border-white/15 bg-white/[.03] px-6 text-white hover:bg-white/10"
                >
                  Explore features
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="bg-[#090a0b] text-white">
          <div className="mx-auto max-w-[1400px] px-5 py-16 lg:px-10 lg:py-20">
            <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1.15fr]">
              <div>
                <div className="flex items-center gap-3 px-3 py-1 rounded-2xl w-fit">
                  <img
                    src="/assets/logo-white.png"
                    alt="StayServe"
                    className="h-12 w-auto object-contain"
                  />

                  <div>
                    <div className="font-display text-[18px] font-black tracking-tight leading-none">
                      <span className="text-[#ffffff]">Stay</span>
                      <span className="text-[#FF6B00]">Serve</span>
                    </div>

                    <div className="mt-1 text-[7px] font-bold uppercase tracking-[.20em] text-[#ffffff]">
                      Hospitality operations
                    </div>
                  </div>
                </div>

                <p className="mt-5 max-w-sm text-sm leading-7 text-stone-500">
                  The connected operating platform for modern hospitality —
                  bringing guest experience, operations, billing, inventory,
                  analytics and future hospitality tools into one ecosystem.
                </p>

                <div className="mt-7 border-t border-white/10 pt-6">
                  <p className="text-[10px] font-bold uppercase tracking-[.22em] text-stone-600">
                    Designed & developed by
                  </p>
                  <p className="mt-2 text-base font-black text-white">
                    SSQUARE LOGIC
                  </p>
                  <p className="mt-1 text-xs leading-5 text-stone-500">
                    IT, software & digital solutions
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-2 text-[10px] text-stone-500">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5">
                    <ShieldCheck size={11} />
                    Secure
                  </span>

                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5">
                    <WifiOff size={11} />
                    Offline-ready
                  </span>

                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5">
                    <Globe2 size={11} />
                    Staff Management
                  </span>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black uppercase tracking-widest">
                  Product
                </h4>

                <div className="mt-5 space-y-3 text-sm text-stone-500">
                  <a
                    href="#platform"
                    className="block transition hover:text-white"
                  >
                    Platform
                  </a>

                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    Features
                  </a>

                  <a href="#how" className="block transition hover:text-white">
                    How it works
                  </a>

                  <a
                    href="#pricing"
                    className="block transition hover:text-white"
                  >
                    Pricing
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black uppercase tracking-widest">
                  Solutions
                </h4>

                <div className="mt-5 space-y-3 text-sm text-stone-500">
                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    POS & Billing
                  </a>

                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    QR Ordering
                  </a>

                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    Kitchen / KDS
                  </a>

                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    Inventory
                  </a>

                  <a
                    href="#features"
                    className="block transition hover:text-white"
                  >
                    Analytics
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black uppercase tracking-widest">
                  Coming soon
                </h4>

                <div className="mt-5 space-y-3 text-sm text-stone-500">
                  <span className="block">Mobile App</span>
                  <span className="block">Hotel Panel</span>
                  <span className="block">Hospitality tools</span>
                  <span className="block">More integrations</span>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black uppercase tracking-widest">
                  Contact & Legal
                </h4>

                <div className="mt-5 space-y-3 text-sm text-stone-500">
                  <a
                    href="mailto:YOUR_EMAIL@example.com"
                    className="block transition hover:text-white"
                  >
                    Contact@ssquarelogic.com
                  </a>
                  <a
                    href="tel:+91XXXXXXXXXX"
                    className="block transition hover:text-white"
                  >
                    +91 97844 77117
                  </a>
                  <span className="block leading-5">
                    Benar Road, Jhotwara, Jaipur, Rajasthan 302012
                  </span>
                </div>

                <div className="mt-7 border-t border-white/10 pt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[.22em] text-stone-600">
                    Legal
                  </p>

                  <div className="space-y-3 text-sm">
                    <Link
                      className="block text-orange-400 transition-colors hover:text-orange-300"
                      to="/privacy-policy"
                    >
                      Privacy Policy
                    </Link>

                    <Link
                      className="block text-stone-400 transition-colors hover:text-white"
                      to="/terms-and-conditions"
                    >
                      Terms & Conditions
                    </Link>

                    <Link
                      className="block text-stone-400 transition-colors hover:text-white"
                      to="/refund-policy"
                    >
                      Refund Policy
                    </Link>

                    <Link
                      className="block text-stone-400 transition-colors hover:text-white"
                      to="/cookie-policy"
                    >
                      Cookie Policy
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-14 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-stone-600 sm:flex-row sm:items-center sm:justify-between">
              <span>
                © 2026 StayServe. All rights reserved. | Designed &amp;
                developed by{" "}
                <a
                  href="https://ssquarelogic.com"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-stone-400 transition-colors"
                >
                  SSQUARE LOGIC
                </a>
                .
              </span>

              <span>
                Built for restaurants &amp; hotels. Designed for the people
                running them.
              </span>
            </div>
          </div>
        </footer>
        {/* Floating WhatsApp Button */}
        <a
          href="https://wa.me/919784477117?text=Hi%20StayServe%2C%20I%20want%20to%20know%20more%20about%20StayServe."
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with StayServe on WhatsApp"
          className="fixed bottom-6 right-6 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl transition-all duration-300 hover:scale-110"
        >
          <MessageCircle size={30} strokeWidth={2.5} />
        </a>
      </main>
    </div>
  );
}
